import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CountrySelect } from '@/components/shared/country-select';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { MemberPhotoUploader } from '@/components/shared/member-photo-uploader';
import { MemberTitleField } from '@/components/shared/member-title-field';
import { Tip } from '@/components/shared/tip';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { PhoneInput } from '@/components/ui/phone-input';
import { SwitchField } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import type { CountryCode } from '@/constants/countries';
import { ApiError } from '@/lib/api-errors';
import { api } from '@/lib/api';
import { memberGenderListboxOptions } from '@/lib/gender';
import { uploadBlob } from '@/lib/upload';
import { useChurchStore } from '@/stores/church-store';

import {
  type BaptismType,
  type Gender,
  type MaritalStatus,
  type MarriageType,
  type MemberFormState,
  type MemberType,
  MARITAL_STATUS_OPTIONS,
  type ReceptionMethod,
} from './member-form-state';
import { MemberSourceFields } from './member-source-fields';

interface FieldsProps {
  value: MemberFormState;
  onChange: (next: MemberFormState) => void;
}

const Section = ({
  title,
  children,
  columns = 2,
}: {
  title: string;
  children: ReactNode;
  columns?: 2 | 3;
}) => (
  <div className="space-y-3">
    <h3 className="text-body-sm font-semibold text-fg">{title}</h3>
    <div
      className={
        columns === 3
          ? 'grid gap-3 sm:grid-cols-2 lg:grid-cols-3'
          : 'grid gap-3 sm:grid-cols-2'
      }
    >
      {children}
    </div>
  </div>
);

const Field = ({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) => (
  <div className={`flex flex-col gap-1.5 ${className ?? ''}`}>
    <Label>{label}</Label>
    {children}
  </div>
);

const FormSection = ({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) => (
  <div className={`space-y-3 ${className ?? ''}`}>
    <h3 className="text-body-sm font-semibold text-fg">{title}</h3>
    {children}
  </div>
);

const SettingsCard = ({ children }: { children: ReactNode }) => (
  <div className="space-y-4 rounded-lg border border-border bg-surface-sunken/40 p-4 pr-5">
    {children}
  </div>
);

/** Full create/edit form — basic identity plus the optional sections. */
export const MemberFormFields = ({ value, onChange }: FieldsProps) => (
  <div className="space-y-6">
    <BasicMemberFields value={value} onChange={onChange} />
    <MoreMemberFields value={value} onChange={onChange} />
  </div>
);

export const BasicMemberFields = ({ value, onChange }: FieldsProps) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const church = useChurchStore((state) => state.church);

  const membershipMode = church?.membershipNumberMode ?? 'manual';
  const membershipPreview =
    membershipMode !== 'manual' && church
      ? `${church.membershipNumberPrefix ?? ''}${String(church.membershipNumberNextValue).padStart(church.membershipNumberPadding, '0')}`
      : null;

  const set = <K extends keyof MemberFormState>(key: K, next: MemberFormState[K]) => {
    onChange({ ...value, [key]: next });
  };

  const displayName = [value.firstName, value.lastName].filter(Boolean).join(' ');

  const photoUploader = (
    <MemberPhotoUploader
      src={value.photoPreviewUrl}
      name={displayName}
      size="xl"
      className="flex-col items-center"
      onUpload={async (blob) => {
        try {
          const { key } = await uploadBlob({ purpose: 'member_photo', blob });
          const preview = URL.createObjectURL(blob);
          if (value.photoPreviewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(value.photoPreviewUrl);
          }
          onChange({ ...value, photoKey: key, photoPreviewUrl: preview });
        } catch (err) {
          toast.error(
            err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'),
          );
          throw err;
        }
      }}
      onRemove={() => {
        if (value.photoPreviewUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(value.photoPreviewUrl);
        }
        onChange({ ...value, photoKey: null, photoPreviewUrl: null });
      }}
      labels={{
        change: t('members.form.photoChange'),
        remove: t('members.form.photoRemove'),
        cropTitle: t('members.form.photoCropTitle'),
        zoom: tCommon('media.zoom'),
        apply: t('members.form.photoApply'),
        cancel: tCommon('action.cancel'),
        instructions: t('members.form.photoInstructions'),
        closeCropper: tCommon('action.close'),
      }}
    />
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="space-y-3">
        <h3 className="text-body-sm font-semibold text-fg">
          {t('members.form.sections.identity')}
        </h3>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="grid min-w-0 flex-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label={t('members.form.membershipNumber')}>
              <Input
                value={value.membershipNumber}
                onChange={(event) => set('membershipNumber', event.target.value)}
                disabled={membershipMode === 'auto'}
                placeholder={membershipPreview ?? undefined}
              />
            </Field>
            <Field label={t('members.form.title')}>
              <MemberTitleField
                value={value.title}
                onChange={(next) => set('title', next)}
                clearLabel={t('members.form.clear')}
                popoverPortal="dialog"
              />
            </Field>
            <Field label={t('members.form.firstName')}>
              <Input
                value={value.firstName}
                onChange={(event) => set('firstName', event.target.value)}
                required
              />
            </Field>
            <Field label={t('members.form.middleName')}>
              <Input
                value={value.middleName}
                onChange={(event) => set('middleName', event.target.value)}
              />
            </Field>
            <Field label={t('members.form.lastName')}>
              <Input
                value={value.lastName}
                onChange={(event) => set('lastName', event.target.value)}
                required
              />
            </Field>
            <Field label={t('members.form.gender')}>
              <Listbox
                value={value.gender}
                onChange={(next) => set('gender', next as Gender | null)}
                options={memberGenderListboxOptions(t)}
                clearable
                clearLabel={t('members.form.clear')}
                popoverPortal="dialog"
              />
            </Field>
            <Field label={t('members.form.dateOfBirth')}>
              <DatePicker
                value={value.dateOfBirth}
                onChange={(next) => set('dateOfBirth', next)}
                popoverPortal="dialog"
              />
            </Field>
            <Field label={t('members.form.memberType')}>
              <Listbox
                value={value.memberType}
                onChange={(next) => set('memberType', (next as MemberType | null) ?? 'member')}
                options={[
                  { value: 'member', label: t('members.type.member') },
                  { value: 'visitor', label: t('members.type.visitor') },
                  { value: 'first_timer', label: t('members.type.first_timer') },
                ]}
                popoverPortal="dialog"
              />
            </Field>
            <Field label={t('members.form.phone')}>
              <PhoneInput
                value={value.phone}
                onChange={(next) => set('phone', next)}
                popoverPortal="dialog"
              />
              {value.memberType === 'first_timer' ? (
                <Tip tipId="members.firstTimerPhone" />
              ) : null}
            </Field>
            <Field label={t('members.form.email')}>
              <Input
                type="email"
                value={value.email}
                onChange={(event) => set('email', event.target.value)}
              />
            </Field>
            <Field label={t('members.form.maritalStatus')}>
              <Listbox
                value={value.maritalStatus}
                onChange={(next) => set('maritalStatus', next as MaritalStatus | null)}
                options={MARITAL_STATUS_OPTIONS.map((status) => ({
                  value: status,
                  label: t(`members.marital.${status}`),
                }))}
                clearable
                clearLabel={t('members.form.clear')}
                popoverPortal="dialog"
              />
            </Field>
            <Field label={t('members.form.isCommunicant')}>
              <Listbox
                value={
                  value.isCommunicant === null ? null : value.isCommunicant ? 'yes' : 'no'
                }
                onChange={(next) =>
                  set('isCommunicant', next === null ? null : next === 'yes')
                }
                options={[
                  { value: 'yes', label: t('members.form.communicantYes') },
                  { value: 'no', label: t('members.form.communicantNo') },
                ]}
                clearable
                clearLabel={t('members.form.clear')}
                popoverPortal="dialog"
              />
            </Field>
            <Field label={t('members.form.joinedAt')}>
              <DatePicker
                value={value.joinedAt}
                onChange={(next) => set('joinedAt', next)}
                popoverPortal="dialog"
              />
            </Field>
          </div>
          <div className="w-[7.5rem] shrink-0 self-center sm:self-start">
            <p className="mb-1.5 text-center text-caption text-fg-secondary">
              {t('members.form.sections.photo')}
            </p>
            {photoUploader}
          </div>
        </div>
      </div>

      <FormSection title={t('members.form.appConnect.title')}>
        <SettingsCard>
          <p className="text-caption text-fg-secondary">{t('members.form.appConnect.description')}</p>
          <SwitchField
            id="app-connect-email-same"
            label={t('members.form.appConnect.emailSameAsContact')}
            checked={value.appConnectEmailSameAsEmail}
            onCheckedChange={(checked) => set('appConnectEmailSameAsEmail', checked)}
          />
          {!value.appConnectEmailSameAsEmail ? (
            <Field label={t('members.form.appConnect.email')}>
              <Input
                type="email"
                value={value.appConnectEmail}
                onChange={(event) => set('appConnectEmail', event.target.value)}
              />
            </Field>
          ) : null}
          <SwitchField
            id="app-connect-phone-same"
            label={t('members.form.appConnect.phoneSameAsContact')}
            checked={value.appConnectPhoneSameAsPhone}
            onCheckedChange={(checked) => set('appConnectPhoneSameAsPhone', checked)}
          />
          {!value.appConnectPhoneSameAsPhone ? (
            <Field label={t('members.form.appConnect.phone')}>
              <PhoneInput
                value={value.appConnectPhone}
                onChange={(next) => set('appConnectPhone', next)}
                popoverPortal="dialog"
              />
            </Field>
          ) : null}
        </SettingsCard>
      </FormSection>

      <FormSection title={t('members.form.sections.care')}>
        <SettingsCard>
          <SwitchField
            id="is-distant-member"
            label={t('members.form.isDistantMember')}
            tipId="members.isDistantMember"
            checked={value.isDistantMember}
            onCheckedChange={(checked) => set('isDistantMember', checked)}
          />
          <SwitchField
            id="has-disability"
            label={t('members.form.hasDisability')}
            tipId="members.hasDisability"
            checked={value.hasDisability}
            onCheckedChange={(checked) => set('hasDisability', checked)}
          />
          {value.hasDisability ? (
            <Field label={t('members.form.disabilityNotes')}>
              <Textarea
                value={value.disabilityNotes}
                onChange={(event) => set('disabilityNotes', event.target.value)}
                rows={2}
              />
            </Field>
          ) : null}
        </SettingsCard>
        <Field label={t('members.form.notes')}>
          <Textarea
            value={value.notes}
            onChange={(event) => set('notes', event.target.value)}
            rows={3}
          />
        </Field>
      </FormSection>
    </div>
  );
};

export const MoreMemberFields = ({ value, onChange }: FieldsProps) => {
  const { t } = useTranslation('people');
  const [spouseQuery, setSpouseQuery] = useState('');
  const [emergencyContactQuery, setEmergencyContactQuery] = useState('');

  const set = <K extends keyof MemberFormState>(key: K, next: MemberFormState[K]) => {
    onChange({ ...value, [key]: next });
  };

  const spouseSearch = useQuery({
    queryKey: ['people', 'spouse-search', spouseQuery],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; firstName: string; lastName: string }> }>(
        `/people/members?q=${encodeURIComponent(spouseQuery)}&pageSize=20&status=active`,
      ),
    enabled: value.maritalStatus === 'married' && value.spouseIsMember,
  });

  const spouseOptions = useMemo(
    () =>
      (spouseSearch.data?.data ?? []).map((row) => ({
        id: row.id,
        label: `${row.firstName} ${row.lastName}`.trim(),
      })),
    [spouseSearch.data?.data],
  );

  const selectedSpouse = spouseOptions.find((row) => row.id === value.spouseMemberId);

  const emergencyContactSearch = useQuery({
    queryKey: ['people', 'emergency-contact-search', emergencyContactQuery],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; firstName: string; lastName: string }> }>(
        `/people/members?q=${encodeURIComponent(emergencyContactQuery)}&pageSize=20&status=active`,
      ),
    enabled: value.emergencyContactIsMember,
  });

  const emergencyContactOptions = useMemo(
    () =>
      (emergencyContactSearch.data?.data ?? []).map((row) => ({
        id: row.id,
        label: `${row.firstName} ${row.lastName}`.trim(),
      })),
    [emergencyContactSearch.data?.data],
  );

  const selectedEmergencyContact = value.emergencyContactMemberId
    ? emergencyContactOptions.find((row) => row.id === value.emergencyContactMemberId) ?? {
        id: value.emergencyContactMemberId,
        label: value.emergencyContactName.trim(),
      }
    : null;

  const showMarriageFields = value.maritalStatus === 'married';

  return (
    <div className="flex flex-col gap-6">
      <Section title={t('members.form.sections.identity')} columns={3}>
        <Field label={t('members.form.maidenName')}>
          <Input value={value.maidenName} onChange={(event) => set('maidenName', event.target.value)} />
        </Field>
        <Field label={t('members.form.placeOfBirth')}>
          <Input
            value={value.placeOfBirth}
            onChange={(event) => set('placeOfBirth', event.target.value)}
          />
        </Field>
        <Field label={t('members.form.motherName')}>
          <Input
            value={value.motherName}
            onChange={(event) => set('motherName', event.target.value)}
          />
        </Field>
        <Field label={t('members.form.fatherName')}>
          <Input
            value={value.fatherName}
            onChange={(event) => set('fatherName', event.target.value)}
          />
        </Field>
        <Field label={t('members.form.nationalId')}>
          <Input
            value={value.nationalId}
            onChange={(event) => set('nationalId', event.target.value)}
          />
        </Field>
        <Field label={t('members.form.alternatePhone')}>
          <PhoneInput
            value={value.alternatePhone}
            onChange={(next) => set('alternatePhone', next)}
            popoverPortal="dialog"
          />
        </Field>
      </Section>

      <Section title={t('members.form.sections.background')} columns={3}>
        <Field label={t('members.form.occupation')} className="sm:col-span-2 lg:col-span-3">
          <Input
            value={value.occupation}
            onChange={(event) => set('occupation', event.target.value)}
          />
        </Field>
        <Field label={t('members.form.employer')} className="sm:col-span-2 lg:col-span-3">
          <Input value={value.employer} onChange={(event) => set('employer', event.target.value)} />
        </Field>
        <Field label={t('members.form.educationBackground')} className="sm:col-span-2 lg:col-span-3">
          <Textarea
            value={value.educationBackground}
            onChange={(event) => set('educationBackground', event.target.value)}
            rows={3}
          />
        </Field>
        <Field label={t('members.form.employmentBackground')} className="sm:col-span-2 lg:col-span-3">
          <Textarea
            value={value.employmentBackground}
            onChange={(event) => set('employmentBackground', event.target.value)}
            rows={3}
          />
        </Field>
      </Section>

      <Section title={t('members.form.sections.residentialAddress')} columns={3}>
        <Field label={t('members.form.residentialAddress')} className="sm:col-span-2 lg:col-span-3">
          <Input value={value.address} onChange={(event) => set('address', event.target.value)} />
        </Field>
        <Field label={t('members.form.city')}>
          <Input value={value.city} onChange={(event) => set('city', event.target.value)} />
        </Field>
        <Field label={t('members.form.region')}>
          <Input value={value.region} onChange={(event) => set('region', event.target.value)} />
        </Field>
        <Field label={t('members.form.country')}>
          <CountrySelect
            value={(value.country as CountryCode | null) ?? null}
            onChange={(next) => set('country', next)}
            priority={['GH']}
            popoverPortal="dialog"
            clearable
            clearLabel={t('members.form.clear')}
          />
        </Field>
        <Field label={t('members.form.hometown')}>
          <Input value={value.hometown} onChange={(event) => set('hometown', event.target.value)} />
        </Field>
        <Field label={t('members.form.hometownRegion')}>
          <Input
            value={value.hometownRegion}
            onChange={(event) => set('hometownRegion', event.target.value)}
          />
        </Field>
      </Section>

      <Section title={t('members.form.sections.postalAddress')} columns={3}>
        <Field label={t('members.form.postalAddress')} className="sm:col-span-2 lg:col-span-3">
          <Input
            value={value.postalAddress}
            onChange={(event) => set('postalAddress', event.target.value)}
          />
        </Field>
        <Field label={t('members.form.postalCity')}>
          <Input
            value={value.postalCity}
            onChange={(event) => set('postalCity', event.target.value)}
          />
        </Field>
        <Field label={t('members.form.postalRegion')}>
          <Input
            value={value.postalRegion}
            onChange={(event) => set('postalRegion', event.target.value)}
          />
        </Field>
        <Field label={t('members.form.postalCountry')}>
          <CountrySelect
            value={(value.postalCountry as CountryCode | null) ?? null}
            onChange={(next) => set('postalCountry', next)}
            priority={['GH']}
            popoverPortal="dialog"
            clearable
            clearLabel={t('members.form.clear')}
          />
        </Field>
      </Section>

      <Section title={t('members.form.sections.membership')} columns={3}>
        <Field label={t('members.form.receptionMethod')}>
          <Listbox
            value={value.receptionMethod}
            onChange={(next) => set('receptionMethod', next as ReceptionMethod | null)}
            options={[
              { value: 'profession', label: t('members.reception.profession') },
              { value: 'confirmation', label: t('members.reception.confirmation') },
              { value: 'transfer', label: t('members.reception.transfer') },
              { value: 'reaffirmation', label: t('members.reception.reaffirmation') },
              { value: 'restoration', label: t('members.reception.restoration') },
              { value: 'birth', label: t('members.reception.birth') },
              { value: 'other', label: t('members.reception.other') },
            ]}
            clearable
            clearLabel={t('members.form.clear')}
            popoverPortal="dialog"
          />
        </Field>
        <Field label={t('members.form.formerChurch')}>
          <Input
            value={value.formerChurch}
            onChange={(event) => set('formerChurch', event.target.value)}
          />
        </Field>
        <div className="sm:col-span-2 lg:col-span-3">
          <MemberSourceFields value={value} onChange={onChange} popoverPortal="dialog" />
        </div>
        <Field label={t('members.form.baptismDate')}>
          <DatePicker
            value={value.baptismDate}
            onChange={(next) => set('baptismDate', next)}
            popoverPortal="dialog"
          />
        </Field>
        <Field label={t('members.form.baptismNumber')}>
          <Input
            value={value.baptismNumber}
            onChange={(event) => set('baptismNumber', event.target.value)}
          />
        </Field>
        <Field label={t('members.form.baptismType')}>
          <Listbox
            value={value.baptismType}
            onChange={(next) => set('baptismType', next as BaptismType | null)}
            options={[
              { value: 'infant', label: t('members.baptismType.infant') },
              { value: 'adult', label: t('members.baptismType.adult') },
              { value: 'unknown', label: t('members.baptismType.unknown') },
            ]}
            clearable
            clearLabel={t('members.form.clear')}
            popoverPortal="dialog"
          />
        </Field>
        <Field label={t('members.form.baptismPlace')}>
          <Input
            value={value.baptismPlace}
            onChange={(event) => set('baptismPlace', event.target.value)}
          />
        </Field>
        <Field label={t('members.form.baptismMinister')}>
          <Input
            value={value.baptismMinister}
            onChange={(event) => set('baptismMinister', event.target.value)}
          />
        </Field>
        <Field label={t('members.form.confirmationDate')}>
          <DatePicker
            value={value.confirmationDate}
            onChange={(next) => set('confirmationDate', next)}
            popoverPortal="dialog"
          />
        </Field>
        <Field label={t('members.form.confirmationNumber')}>
          <Input
            value={value.confirmationNumber}
            onChange={(event) => set('confirmationNumber', event.target.value)}
          />
        </Field>
        <Field label={t('members.form.confirmationPlace')}>
          <Input
            value={value.confirmationPlace}
            onChange={(event) => set('confirmationPlace', event.target.value)}
          />
        </Field>
      </Section>

      {showMarriageFields ? (
        <Section title={t('members.form.sections.marriage')} columns={3}>
          <div className="flex flex-col gap-3 sm:col-span-2 lg:col-span-3">
            <SwitchField
              id="spouse-is-member"
              label={t('members.form.spouseIsMember')}
              checked={value.spouseIsMember}
              onCheckedChange={(checked) =>
                onChange({
                  ...value,
                  spouseIsMember: checked,
                  spouseMemberId: checked ? value.spouseMemberId : null,
                  spouseNameManual: checked ? '' : value.spouseNameManual,
                })
              }
            />
            {value.spouseIsMember ? (
              <Field label={t('members.form.spouseMember')}>
                <EntityCombobox
                  value={value.spouseMemberId}
                  onChange={(memberId) => set('spouseMemberId', memberId)}
                  options={spouseOptions}
                  selected={selectedSpouse ?? null}
                  onQueryChange={setSpouseQuery}
                  loading={spouseSearch.isFetching}
                  placeholder={t('members.form.spouseMemberPlaceholder')}
                  emptyMessage={t('members.form.spouseMemberEmpty')}
                  clearLabel={t('members.form.clear')}
                  popoverPortal="dialog"
                />
              </Field>
            ) : (
              <Field label={t('members.form.spouseNameManual')}>
                <Input
                  value={value.spouseNameManual}
                  onChange={(event) => set('spouseNameManual', event.target.value)}
                />
              </Field>
            )}
          </div>
          <Field label={t('members.form.marriageDate')}>
            <DatePicker
              value={value.marriageDate}
              onChange={(next) => set('marriageDate', next)}
              popoverPortal="dialog"
            />
          </Field>
          <Field label={t('members.form.marriageType')}>
            <Listbox
              value={value.marriageType}
              onChange={(next) => set('marriageType', next as MarriageType | null)}
              options={[
                { value: 'customary', label: t('members.marriageType.customary') },
                { value: 'ordinance', label: t('members.marriageType.ordinance') },
                { value: 'civil', label: t('members.marriageType.civil') },
                { value: 'both', label: t('members.marriageType.both') },
                { value: 'other', label: t('members.marriageType.other') },
              ]}
              clearable
              clearLabel={t('members.form.clear')}
              popoverPortal="dialog"
            />
          </Field>
          <Field label={t('members.form.marriagePlace')}>
            <Input
              value={value.marriagePlace}
              onChange={(event) => set('marriagePlace', event.target.value)}
            />
          </Field>
          <Field label={t('members.form.marriageMinister')}>
            <Input
              value={value.marriageMinister}
              onChange={(event) => set('marriageMinister', event.target.value)}
            />
          </Field>
        </Section>
      ) : null}

      <Section title={t('members.form.sections.emergency')} columns={3}>
        <div className="flex flex-col gap-3 sm:col-span-2 lg:col-span-3">
          <SwitchField
            id="emergency-contact-is-member"
            label={t('members.form.emergencyContactIsMember')}
            checked={value.emergencyContactIsMember}
            onCheckedChange={(checked) =>
              onChange({
                ...value,
                emergencyContactIsMember: checked,
                emergencyContactMemberId: checked ? value.emergencyContactMemberId : null,
                emergencyContactName: checked ? '' : value.emergencyContactName,
                emergencyContactPhone: checked ? null : value.emergencyContactPhone,
              })
            }
          />
          {value.emergencyContactIsMember ? (
            <Field label={t('members.form.emergencyContactMember')}>
              <EntityCombobox
                value={value.emergencyContactMemberId}
                onChange={(memberId) => set('emergencyContactMemberId', memberId)}
                options={emergencyContactOptions}
                selected={selectedEmergencyContact?.label ? selectedEmergencyContact : null}
                onQueryChange={setEmergencyContactQuery}
                loading={emergencyContactSearch.isFetching}
                placeholder={t('members.form.emergencyContactMemberPlaceholder')}
                emptyMessage={t('members.form.emergencyContactMemberEmpty')}
                clearLabel={t('members.form.clear')}
                popoverPortal="dialog"
              />
            </Field>
          ) : (
            <>
              <Field label={t('members.form.emergencyContactName')}>
                <Input
                  value={value.emergencyContactName}
                  onChange={(event) => set('emergencyContactName', event.target.value)}
                />
              </Field>
              <Field label={t('members.form.emergencyContactPhone')}>
                <PhoneInput
                  value={value.emergencyContactPhone}
                  onChange={(next) => set('emergencyContactPhone', next)}
                  popoverPortal="dialog"
                />
              </Field>
            </>
          )}
        </div>
        <Field label={t('members.form.emergencyContactRelationship')}>
          <Input
            value={value.emergencyContactRelationship}
            onChange={(event) => set('emergencyContactRelationship', event.target.value)}
          />
        </Field>
      </Section>
    </div>
  );
};
